import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import {
  Avatar,
  Box,
  CircularProgress,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import type {
  StatusBreakdown,
  UserAllocationDeal,
} from "../../../types/userAllocations";
import {
  getUserAllocationCombinationDeals,
  type DealsActiveFilter,
} from "../../userAllocations/userAllocationsApi";
import { getColorForName, getInitials } from "../../../utils/noteUtils";
import {
  buildCombinationDealsCacheKey,
  formatCount,
  type CombinationSelection,
} from "./combinationUtils";

const PIPELINE_ROW_HOVER_COLOR = "#e1efea";

const PRIORITY_COLORS: Record<string, string> = {
  High: "#4caf50",
  Medium: "#ffb300",
  Low: "#f44336",
};

const formatGbp = (value: number) =>
  new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);

const formatProbability = (value: number | null) =>
  value == null ? "—" : `${value}%`;

function statusRowKey(row: StatusBreakdown) {
  return `${row.id ?? "null"}-${row.name}-${row.sort_order}`;
}

function AssigneeAvatar({ name }: { name: string | null }) {
  if (!name) {
    return <>—</>;
  }

  return (
    <Tooltip title={name} arrow>
      <Avatar
        sx={{
          width: 28,
          height: 28,
          fontSize: "0.65rem",
          fontWeight: 700,
          bgcolor: getColorForName(name),
        }}
      >
        {getInitials(name)}
      </Avatar>
    </Tooltip>
  );
}

const expandedDealsTableSx = {
  tableLayout: "fixed",
  width: "100%",
  "& .MuiTableCell-root": {
    fontSize: 13,
    px: 1.25,
    py: 0.875,
    lineHeight: 1.3,
  },
  "& .MuiTableCell-head": {
    fontWeight: 600,
    fontSize: 12,
    color: "#182737",
    whiteSpace: "nowrap",
  },
} as const;

type CombinationStatusBreakdownTableProps = {
  selection: CombinationSelection;
  dealsActive: DealsActiveFilter;
  statusIds?: number[];
  cacheResetKey: string;
  onFetchError: (message: string) => void;
};

export function CombinationStatusBreakdownTable({
  selection,
  dealsActive,
  statusIds,
  cacheResetKey,
  onFetchError,
}: CombinationStatusBreakdownTableProps) {
  const rows = selection.byStatus;
  const [expandedRowKeys, setExpandedRowKeys] = useState<Set<string>>(
    () => new Set(),
  );
  const [dealsCache, setDealsCache] = useState<Map<string, UserAllocationDeal[]>>(
    () => new Map(),
  );
  const [loadingKeys, setLoadingKeys] = useState<Set<string>>(() => new Set());
  const dealsCacheRef = useRef(dealsCache);
  dealsCacheRef.current = dealsCache;

  useEffect(() => {
    setExpandedRowKeys(new Set());
    setDealsCache(new Map());
    setLoadingKeys(new Set());
  }, [cacheResetKey]);

  const fetchDeals = useCallback(
    async (row: StatusBreakdown, cacheKey: string) => {
      setLoadingKeys((prev) => new Set(prev).add(cacheKey));
      try {
        const response = await getUserAllocationCombinationDeals({
          dealsActive,
          statusIds,
          ...(selection.leadUnassigned
            ? { leadUnassigned: true }
            : selection.leadUserId != null
              ? { leadUserId: selection.leadUserId }
              : {}),
          ...(selection.supportUnassigned
            ? { supportUnassigned: true }
            : selection.supportUserId != null
              ? { supportUserId: selection.supportUserId }
              : {}),
          ...(row.id == null
            ? { statusUnassigned: true }
            : { statusId: row.id }),
        });

        if (response.success && Array.isArray(response.data)) {
          setDealsCache((prev) => new Map(prev).set(cacheKey, response.data!));
          return;
        }

        onFetchError("Failed to load deals for this status.");
        setDealsCache((prev) => new Map(prev).set(cacheKey, []));
      } catch {
        onFetchError("Failed to load deals for this status. Please try again.");
        setDealsCache((prev) => new Map(prev).set(cacheKey, []));
      } finally {
        setLoadingKeys((prev) => {
          if (!prev.has(cacheKey)) return prev;
          const next = new Set(prev);
          next.delete(cacheKey);
          return next;
        });
      }
    },
    [dealsActive, onFetchError, selection, statusIds],
  );

  const handleToggle = useCallback(
    (row: StatusBreakdown) => {
      if (row.deal_count <= 0) return;

      const rowKey = statusRowKey(row);
      const cacheKey = buildCombinationDealsCacheKey({
        leadUserId: selection.leadUserId,
        leadUnassigned: selection.leadUnassigned,
        supportUserId: selection.supportUserId,
        supportUnassigned: selection.supportUnassigned,
        statusRow: row,
        dealsActive,
        statusIds,
      });

      const isExpanding = !expandedRowKeys.has(rowKey);

      setExpandedRowKeys((prev) => {
        const next = new Set(prev);
        if (isExpanding) {
          next.add(rowKey);
        } else {
          next.delete(rowKey);
        }
        return next;
      });

      if (isExpanding && !dealsCacheRef.current.has(cacheKey)) {
        void fetchDeals(row, cacheKey);
      }
    },
    [dealsActive, expandedRowKeys, fetchDeals, selection, statusIds],
  );

  if (rows.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No deals for this pairing under the current filters.
      </Typography>
    );
  }

  return (
    <TableContainer>
      <Table size="small" sx={{ tableLayout: "fixed", width: "100%" }}>
        <TableHead>
          <TableRow>
            <TableCell>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Box sx={{ width: 20, flexShrink: 0 }} aria-hidden />
                Status
              </Box>
            </TableCell>
            <TableCell align="right" sx={{ width: 72 }}>
              Deals
            </TableCell>
            <TableCell align="right" sx={{ width: 128 }}>
              Volume (£)
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => {
            const rowKey = statusRowKey(row);
            const isExpanded = expandedRowKeys.has(rowKey);
            const canExpand = row.deal_count > 0;
            const cacheKey = buildCombinationDealsCacheKey({
              leadUserId: selection.leadUserId,
              leadUnassigned: selection.leadUnassigned,
              supportUserId: selection.supportUserId,
              supportUnassigned: selection.supportUnassigned,
              statusRow: row,
              dealsActive,
              statusIds,
            });
            const deals = dealsCache.get(cacheKey);
            const isLoading = loadingKeys.has(cacheKey);

            return (
              <Fragment key={rowKey}>
                <TableRow
                  hover={false}
                  onClick={() => handleToggle(row)}
                  sx={{
                    cursor: canExpand ? "pointer" : "default",
                    ...(canExpand && {
                      "&:hover": { backgroundColor: PIPELINE_ROW_HOVER_COLOR },
                    }),
                    "& > *": {
                      borderBottom: isExpanded ? "none" : undefined,
                    },
                  }}
                >
                  <TableCell sx={{ fontWeight: isExpanded ? 700 : undefined }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        fontWeight: isExpanded ? 700 : undefined,
                      }}
                    >
                      {canExpand ? (
                        isExpanded ? (
                          <ExpandLessIcon
                            fontSize="small"
                            sx={{ color: "text.secondary" }}
                          />
                        ) : (
                          <ExpandMoreIcon
                            fontSize="small"
                            sx={{ color: "text.secondary" }}
                          />
                        )
                      ) : (
                        <Box sx={{ width: 20 }} />
                      )}
                      {row.name}
                    </Box>
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ width: 72, fontWeight: isExpanded ? 700 : undefined }}
                  >
                    {formatCount(row.deal_count)}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ width: 128, fontWeight: isExpanded ? 700 : undefined }}
                  >
                    {formatGbp(row.loan_amount_gbp)}
                  </TableCell>
                </TableRow>
                {canExpand && (
                  <TableRow>
                    <TableCell
                      colSpan={3}
                      sx={{
                        py: 0,
                        px: 0,
                        borderBottom: isExpanded ? undefined : "none",
                      }}
                    >
                      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <Box sx={{ px: 1, py: 1, bgcolor: "#f8f9fa" }}>
                          {isLoading ? (
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "center",
                                py: 1.5,
                              }}
                            >
                              <CircularProgress size={22} />
                            </Box>
                          ) : deals && deals.length === 0 ? (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ px: 1 }}
                            >
                              No deals found.
                            </Typography>
                          ) : deals ? (
                            <Table size="small" sx={expandedDealsTableSx}>
                              <TableHead>
                                <TableRow>
                                  <TableCell sx={{ width: "34%" }}>Deal</TableCell>
                                  <TableCell
                                    align="center"
                                    sx={{ width: "10%" }}
                                  >
                                    Lead
                                  </TableCell>
                                  <TableCell
                                    align="center"
                                    sx={{ width: "10%" }}
                                  >
                                    Support
                                  </TableCell>
                                  <TableCell
                                    align="center"
                                    sx={{ width: "14%" }}
                                  >
                                    Priority
                                  </TableCell>
                                  <TableCell
                                    align="center"
                                    sx={{ width: "13%" }}
                                  >
                                    Probability
                                  </TableCell>
                                  <TableCell
                                    align="right"
                                    sx={{ width: "19%" }}
                                  >
                                    Amount (£)
                                  </TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {deals.map((deal) => (
                                  <TableRow key={deal.id}>
                                    <TableCell
                                      sx={{ width: "34%", maxWidth: "34%" }}
                                    >
                                      <Typography
                                        component="a"
                                        href={`/deals/${deal.id}`}
                                        title={deal.deal_name || `ID ${deal.id}`}
                                        sx={{
                                          color: "#1B7A63",
                                          textDecoration: "underline",
                                          display: "block",
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          whiteSpace: "nowrap",
                                          fontSize: 13,
                                        }}
                                      >
                                        {deal.deal_name || `ID ${deal.id}`}
                                      </Typography>
                                    </TableCell>
                                    <TableCell align="center" sx={{ width: "10%" }}>
                                      <Box
                                        sx={{
                                          display: "flex",
                                          justifyContent: "center",
                                        }}
                                      >
                                        <AssigneeAvatar
                                          name={deal.deal_lead_name ?? null}
                                        />
                                      </Box>
                                    </TableCell>
                                    <TableCell align="center" sx={{ width: "10%" }}>
                                      <Box
                                        sx={{
                                          display: "flex",
                                          justifyContent: "center",
                                        }}
                                      >
                                        <AssigneeAvatar
                                          name={deal.deal_support_name ?? null}
                                        />
                                      </Box>
                                    </TableCell>
                                    <TableCell align="center" sx={{ width: "14%" }}>
                                      {deal.priority ? (
                                        <Typography
                                          sx={{
                                            color:
                                              PRIORITY_COLORS[deal.priority] ??
                                              "text.primary",
                                            fontSize: 13,
                                            fontWeight: 600,
                                          }}
                                        >
                                          {deal.priority}
                                        </Typography>
                                      ) : (
                                        "—"
                                      )}
                                    </TableCell>
                                    <TableCell align="center" sx={{ width: "13%" }}>
                                      {formatProbability(deal.probability)}
                                    </TableCell>
                                    <TableCell align="right" sx={{ width: "19%" }}>
                                      {formatGbp(deal.loan_amount_gbp)}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          ) : null}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                )}
              </Fragment>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
